import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import Lottie from 'react-lottie';
import nokiaLottie from '../assets/lottie/nokia-graphic-desktop.json';
import nokiaLottieM from '../assets/lottie/nokia-graphic-mobile.json';
import nokiaLottie2 from '../assets/lottie/nokiaLottie1.json';
import nokiaLottie2M from '../assets/lottie/nokiaLottie1M.json';

import pullQuote1 from '../assets/pullQuote1.png';
import pullQuote1M from '../assets/pullQuote1M.png';
import stat1 from '../assets/stat1.png';
import stat1M from '../assets/stat1M.png';
import tempCloudImg from '../assets/tempCloudImg.jpg';

const Body = () => {
  const { ref, inView } = useInView();
  const [width, setWidth] = useState(window.innerWidth);

  const breakpoints = {
    sm: 500,
    tab: 768,
    md: 1024,
    lg: 1280,
    xl: 1440
  };

  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: nokiaLottie,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };
  const defaultOptionsM = {
    loop: true,
    autoplay: true,
    animationData: nokiaLottieM,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };
  const defaultOptions2 = {
    loop: true,
    autoplay: true,
    animationData: nokiaLottie2,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };
  const defaultOptions2M = {
    loop: true,
    autoplay: true,
    animationData: nokiaLottie2M,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  return (
    <div className="body">
      <div className="body__paragraph">
        <p>
          Today’s enterprise apps cross clouds and borders. The new normal in IT is{' '}
          <span className="pI">hybrid</span>—a mash-up of on-premises datacenter assets, cloud
          technologies, and computing at the edge. The payoff of a more dispersed and complex IT
          environment is the agility and scale organizations need to innovate quickly.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          A few years ago, inventors at the Finland-based global telecommunications giant Nokia
          developed a groundbreaking AI application suite called Nokia AVA. This suite gathers data
          and delivers AI-based insights to help Nokia’s customers—big telco carriers—to run their
          businesses more efficiently.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          These customers can store and analyze more than 100 billion data points a day on a single
          network.<sup>1</sup> The benefits go beyond timely operational insights. Carriers can also
          choose the cloud service on which to run AVA while monitoring and operating everything
          from one place.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Apps without borders, like AVA, can reach more customers and deliver truly innovative
          solutions. But there are considerations, including how to govern and secure these complex
          environments.
        </p>
      </div>
      <div ref={ref} className="body__lottie">
        {/* {inView ? ( */}
        <Lottie
          options={width <= breakpoints.sm ? defaultOptionsM : defaultOptions}
          className="lottie"
        />
        {/* ) : null} */}
      </div>
      <div className="body__paragraph">
        <p>
          Roanne Sones, Corporate Vice President of Azure Edge and Platform at Microsoft, has seen
          an evolution in cloud strategies across organizations. “What we know from customers that
          are modernizing their business and operations is that they need to distribute
          cloud-powered apps and computing power throughout their organization. However, as they do
          that, they face significant challenges to innovation. They need to address latency
          requirements, maintain industry regulatory compliance, and ensure a holistic security
          approach.”
        </p>
      </div>
      <div className="body__paragraph">
        <h6>Different industries, similar changes</h6>
        <p>
          Sones observes that leaders want technology that gives them the flexibility to innovate
          anywhere in their environment, but “the complexity can become overwhelming.” Facing the
          modern technology sprawl is a bit like confronting the piles of stuff that seem to
          accumulate in your closets and garage. Where do you start?
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          “They may have multiple teams using different development tools, languages, and
          frameworks,” Sones adds. “The absence of a unified strategy is keeping data and processes
          siloed and slows innovation.”
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          The first step, she advises, is to find a consistent way to manage that complexity.
          Companies like Nokia need a strategy that can span across multiple clouds. Others need to
          extend that consistency across the cloud and to their on-premises environment.
          Organizations also need the flexibility to bring cloud services down to earth for security
          and regulatory reasons without compromising innovation in the process.
        </p>
      </div>
      <div className="body__pullQuote">
        <img src={width <= breakpoints.sm ? pullQuote1M : pullQuote1} alt="box with quote" />
      </div>
      <div className="body__paragraph">
        <p>
          If those challenges sound familiar, just know that there are choices. Nokia found a
          solution for addressing the data regulatory needs of the different locales where AVA runs
          in Nokia’s partnership with Microsoft. Nokia decided to host AVA on Microsoft Azure, which
          serves more geographies than any other cloud provider.<sup>2</sup>
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Another solution is to follow best practices in hybrid and multicloud management. Nokia
          uses Azure Arc to provide that much-needed centralized visibility and control.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          “That simplifies life for us and our customers,” says Paolo Tornaghi, Technology and
          Architecture CoE Leader in the Cloud and Network Services group at Nokia. It’s an
          app-centric approach to managing virtual infrastructures and services. Azure Arc leverages
          tools customers are already using and are familiar with, giving organizations more control
          over the pace of their cloud journeys.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Azure Arc extends the features and services that make the cloud so attractive to an
          organization’s entire IT environment, even if that includes other clouds. Azure services
          and applications can run anywhere—on-premises, across clouds, and at the edge. Nokia used
          this approach to give its teams the power to explore options that are enabled on the cloud
          today—but do so on-premises.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Organizations can also run Azure Arc–enabled data and application services anywhere and
          get the benefits of an always-current environment. Development teams benefit from faster
          app cycles, speedy deployment, and virtually limitless scalability.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Nokia Head of Cognitive Services Practice Morena Ferrario notes, “Azure Arc delivered a
          consistent way to deploy, operate, and monitor the Nokia AVA use cases across any
          customer’s preferred cloud.”
        </p>
      </div>
      <div className="body__photoBlock">
        <img src={tempCloudImg} alt="digital cloud" />
      </div>
      <div className="body__paragraph">
        <h6>The need for speed</h6>
        <p>
          When it comes to running AI, as Nokia does—or running any other compute-heavy workloads in
          hybrid cloud environments—speed is essential. That is why hardware still matters in the
          virtual world.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          According to Rose Schooler, Corporate Vice President Global Data Center Sales at Intel,
          the partnership between Microsoft and Intel is helping “to provide solutions to your
          toughest business challenges. This includes designing a hybrid platform that can handle
          all your data needs, allowing you to focus on turning insights into action.”
        </p>
      </div>
      <div className="body__splitCont">
        <div className="paragraphCont">
          <p>
            This type of partnership is an example of what to look for when shopping for hybrid
            cloud platforms. Options like Azure Stack HCI create a virtual datacenter—a
            hyper-converged infrastructure (HCI). Intel processors offer the speed that
            organizations need to add trusted enterprise virtualization, connect remote or branch
            offices and the edge, support a virtual desktop infrastructure (VDI), or deploy AI
            services. The 3rd Gen Intel® Xeon® Scalable processors, the latest from Intel, takes
            these capabilities even further by delivering significant analytical performance
            improvements over previous generations.
          </p>
        </div>
        <div className="statCont">
          <img src={width <= breakpoints.sm ? stat1M : stat1} alt="statistic" />
        </div>
      </div>
      <div className="body__paragraph">
        <h6>Without sacrificing security</h6>
        <p>
          It goes without saying that all the data powering hybrid cloud apps like Nokia AVA must be
          protected both at rest and in transit—no matter what. When data travels across on-premises
          and cloud infrastructures, or across clouds, it’s hard to get a consistent operational
          view for governance and access control. Consistency helps organizations overcome three
          common hurdles: data and process management, regulatory compliance, and security.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Consistency is also needed to automate these complex environments. Automation can improve
          not just quality but also security.
        </p>
      </div>
      <div ref={ref} className="body__lottie">
        {/* {inView ? ( */}
        <Lottie
          options={width <= breakpoints.sm ? defaultOptions2M : defaultOptions2}
          className="lottie"
        />
        {/* ) : null} */}
      </div>
      <div className="body__paragraph">
        <p>
          Even as the data landscape continues to expand in size and complexity, security must stay
          two steps ahead. Microsoft advises enterprises to take a multilayered approach: protect
          infrastructure, defend against threats, control access, and safeguard the deployment and
          operations life cycle—whether on-premises, in the cloud, or at edge sites.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          For Nokia AVA, staying two steps ahead also means relying on the built-in security
          measures of its cloud-hosting partner. Microsoft invests more than $1 billion in security
          each year to create a highly secure foundation for Azure.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          In addition, Nokia uses Azure Defender together with Azure Arc to enhance its overall
          security posture. Vulnerability assessments help Nokia to take proactive steps to protect
          itself and its customers before any kind of breach can happen.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          This approach follows the unified strategy that Sones advises, and it extends to customer
          data. The right solution can keep tabs on compliance while using automation to streamline
          those time-consuming, error-prone cloud management tasks. Nokia AVA uses Azure Arc with
          Azure Monitor to bring together metrics, logs, and information from across
          environments—including on-premises hard drives, various cloud servers, edge computing, and
          even Nokia’s Kubernetes clusters—and to run operations from a single spot.
        </p>
      </div>
      <div className="body__paragraph">
        <p>
          Nokia’s experience reflects the trends that Microsoft and Intel are seeing among their
          customers. A hybrid cloud strategy can help organizations keep up with the modern pace of
          innovation.
        </p>
      </div>
      <a
        href="https://azure.microsoft.com/en-us/services/azure-arc/hybrid-data-services/#overview"
        target="_blank"
        rel="sponsored noopener"
      >
        <div className="body__cta">
          <div className="ctaText">
            <p> Azure Arc–Enabled Data Services & Management | Microsoft Azure</p>
          </div>
          <div className="ctaButton">
            <button>Learn More</button>
          </div>
        </div>
      </a>
    </div>
  );
};

export default Body;
